import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export const SIDEBAR_IS_OPEN = 'SIDEBAR_IS_OPEN';
export const SIDEBAR_AUTO_COLLAPSE = 'SIDEBAR_AUTO_COLLAPSE';
export const SIDEBAR_WIDTH = 'SIDEBAR_WIDTH';
export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 420;
export const SIDEBAR_DEFAULT_WIDTH = 250;

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  readonly minWidth = SIDEBAR_MIN_WIDTH;
  readonly maxWidth = SIDEBAR_MAX_WIDTH;
  readonly defaultWidth = SIDEBAR_DEFAULT_WIDTH;

  readonly isOpen = signal(true);
  readonly isSubmenuOpen = signal(false);
  readonly isResponsive = signal(false);
  readonly isAutoCollapseEnabled = signal(true);
  readonly sidebarWidth = signal<number>(SIDEBAR_DEFAULT_WIDTH);
  readonly isResizing = signal<boolean>(false);

  private readonly platformId = inject(PLATFORM_ID);
  private resizeObserver: ResizeObserver | null = null;
  private readonly breakpoint = 768;
  private wasSmallScreen: boolean | null = null;
  private lastWindowWidth = 0;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeSidebar();
      this.setupResponsiveBehavior();
    }
  }

  private initializeSidebar(): void {
    const savedIsOpen = localStorage.getItem(SIDEBAR_IS_OPEN);
    const savedAutoCollapse = localStorage.getItem(SIDEBAR_AUTO_COLLAPSE);

    if (savedIsOpen) {
      this.isOpen.set(JSON.parse(savedIsOpen));
    }

    if (savedAutoCollapse) {
      this.isAutoCollapseEnabled.set(JSON.parse(savedAutoCollapse));
    }

    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH);
    if (savedWidth) {
      const parsed = Number(savedWidth);
      if (!Number.isNaN(parsed)) {
        this.sidebarWidth.set(
          Math.min(this.maxWidth, Math.max(this.minWidth, Math.round(parsed))),
        );
      }
    }
  }

  private setupResponsiveBehavior(): void {
    // Check initial screen size
    this.checkScreenSize();

    // Setup ResizeObserver for modern browsers
    if (typeof ResizeObserver === 'undefined') {
      // Fallback to window resize event
      window.addEventListener('resize', () => {
        this.checkScreenSize();
      });
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (window.innerWidth !== this.lastWindowWidth) {
        this.checkScreenSize();
      }
    });

    this.resizeObserver.observe(document.body);
  }

  private checkScreenSize(): void {
    const currentWidth = window.innerWidth;
    const isSmallScreen = currentWidth < this.breakpoint;
    const crossedIntoSmallScreen =
      this.wasSmallScreen === null
        ? isSmallScreen
        : !this.wasSmallScreen && isSmallScreen;

    this.wasSmallScreen = isSmallScreen;
    this.lastWindowWidth = currentWidth;
    this.isResponsive.set(isSmallScreen);

    // Auto-collapse only when entering small screen or on initial small screen load,
    // never on DOM body height changes after the user manually expanded the sidebar on mobile.
    if (
      crossedIntoSmallScreen &&
      this.isAutoCollapseEnabled() &&
      this.isOpen()
    ) {
      this.collapse();
    }
  }

  toggle(): void {
    this.isOpen.set(!this.isOpen());
    localStorage.setItem(SIDEBAR_IS_OPEN, JSON.stringify(this.isOpen()));
  }

  expand(): void {
    this.isOpen.set(true);
    localStorage.setItem(SIDEBAR_IS_OPEN, JSON.stringify(true));
  }

  collapse(): void {
    this.isOpen.set(false);
    localStorage.setItem(SIDEBAR_IS_OPEN, JSON.stringify(false));
  }

  toggleAutoCollapse(): void {
    this.isAutoCollapseEnabled.set(!this.isAutoCollapseEnabled());
    localStorage.setItem(
      SIDEBAR_AUTO_COLLAPSE,
      JSON.stringify(this.isAutoCollapseEnabled()),
    );
  }

  shouldPreventAutoExpansion(): boolean {
    return this.isResponsive() && !this.isOpen();
  }

  setSidebarWidth(width: number): void {
    const clamped = Math.min(
      this.maxWidth,
      Math.max(this.minWidth, Math.round(width)),
    );
    this.sidebarWidth.set(clamped);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(SIDEBAR_WIDTH, String(clamped));
    }
  }

  resetSidebarWidth(): void {
    this.setSidebarWidth(this.defaultWidth);
  }

  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
